import cx from 'classnames';
import { useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { ampli } from '../../../../../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import {
    TrackingProperties,
    useTrackEventAmplitude,
} from '../../../../../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../../../../../design-system/components/form/button/Button';
import {
    Select,
    SelectProps,
} from '../../../../../../../design-system/components/form/select/Select';
import { Destination } from '../../../../../../../resources/destination/domain/types/destination';
import { useUrlGenerator } from '../../../../../../../shared/hooks/useUrlGenerator';
import { convertDateToUTC } from '../../../../../../../shared/utilities/date';
import { PickDomain } from '../../../../../../../shared/utilities/types/PickDomain';

import style from './CoverFormFacet.module.css';

type Props = {
    destination: PickDomain<Destination, 'slug' | 'id'>;
    trackingInformation: {
        tripFormContactAgency: TrackingProperties<typeof ampli.tripformCreationStartedFromFacet>;
    };
    isOld?: boolean;
    isFamilyFacet: boolean;
};

const SELECT_DATE_MAX_ITEM = 22;

export function CoverFormFacet({ destination, trackingInformation, isOld, isFamilyFacet }: Props) {
    const [travellerSelected, setTravellerSelected] = useState(isFamilyFacet ? 'family' : '');
    const [dateSelected, setDateSelected] = useState('');
    const { formatDate, formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();

    function selectAllDatesOptions() {
        const todayUTC = convertDateToUTC(new Date());
        const startDate = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);

        return Array.from(Array(SELECT_DATE_MAX_ITEM).keys()).reduce<SelectProps['options']>(
            (acc, i) => {
                const newDate = new Date(startDate);
                const month = newDate.getMonth() + i + 1;
                newDate.setMonth(month);

                const yearValue = newDate.getFullYear();
                const monthValue = (newDate.getMonth() + 1).toString().padStart(2, '0');
                const dayValue = '01';

                acc.push({
                    value: `${yearValue}-${monthValue}-${dayValue}`,
                    label: formatDate(newDate, {
                        year: 'numeric',
                        month: 'long',
                    }),
                });
                return acc;
            },
            [],
        );
    }

    return (
        <div
            className={cx(style.formWrapper, {
                [style.isOld]: isOld,
            })}
        >
            <div className={style.selectWrapper}>
                <div className={style.travelerSelect}>
                    <span className={cx(style.travelerSelectLabel, 'font-body-bold')}>
                        {formatMessage({
                            id: 'page.facet.cover.travelWith',
                        })}
                    </span>
                    <Select
                        className={style.select}
                        placeholder={formatMessage({
                            id: 'page.facet.cover.travelersPlaceholder',
                        })}
                        options={['couple', 'family', 'friends', 'group', 'alone'].map((value) => {
                            return {
                                value,
                                label: formatMessage({
                                    id: `page.facet.cover.travellerSelect.${value}` as MessageDescriptor['id'],
                                }),
                            };
                        })}
                        setSelected={setTravellerSelected}
                        selected={travellerSelected}
                    />
                </div>
                <div className={style.dateSelect}>
                    <span className={cx(style.dateSelectLabel, 'font-body-bold')}>
                        {formatMessage({ id: 'page.facet.cover.travelIn' })}
                    </span>

                    <Select
                        className={style.select}
                        placeholder={formatMessage({
                            id: 'page.facet.cover.datePlaceholder',
                        })}
                        options={selectAllDatesOptions()}
                        setSelected={setDateSelected}
                        selected={dateSelected}
                    />
                </div>
            </div>
            <div className={style.buttonWrapper}>
                <Button
                    obfuscate
                    className={style.button}
                    onClick={() => {
                        trackEvent((ampli, defaultProperties) => {
                            ampli.tripformCreationStartedFromFacet({
                                ...defaultProperties,
                                ...trackingInformation.tripFormContactAgency,
                            });
                        });
                    }}
                    href={generatePath('trip_form', {
                        destinationSlug: destination.slug,
                        traveler: travellerSelected || undefined,
                        dates: dateSelected || undefined,
                    })}
                >
                    {formatMessage({ id: 'global.cta.tripForm.default' })}
                </Button>
            </div>
        </div>
    );
}
